import React from 'react';
import { useBrand } from '../../contexts/BrandContext';

/**
 * BrandMark — the brand logo, drawn as vector rather than loaded as a file.
 *
 * WHY THIS EXISTS
 * BRAND_CONFIG points `logo` at /assets/tradesmatepro-logo.png and
 * /assets/smbmate-logo.png, and `favicon` at two .ico files. NONE of those four files
 * exist — apps/contractor-app/public/assets contains only `dashboard/` and a README.
 * BrandedLogo hid the broken <img> in an onError handler, so the failure was invisible:
 * both brands silently rendered as text with no mark at all, and had done for as long
 * as the config has existed.
 *
 * Drawn instead of shipped because a mark that reads the brand's own colours can never
 * 404, is crisp at every size (a 32px favicon and a 512px app icon are the same code),
 * needs no per-brand asset pipeline, and stays correct when a brand's colour changes.
 *
 * The glyph is a check inside a rounded tile: deliberately about COMPLETED WORK, which
 * is what both products are for, rather than about tools — the trade iconography
 * elsewhere in the app is precisely what does not survive white-labelling.
 *
 * If a real designed logo ever lands, set BRAND_CONFIG.logo to it and BrandedLogo will
 * prefer the file; this stays as the fallback.
 */
export default function BrandMark({ className = 'h-8 w-8', title }) {
  const { brand } = useBrand();

  const primary = brand?.primaryColor || '#2563EB';
  const secondary = brand?.secondaryColor || primary;

  // Gradient ids must be unique per brand, or two marks rendered on one page (e.g. a
  // brand switcher) would both paint whichever gradient mounted first.
  const gradientId = `brand-mark-gradient-${brand?.code || 'default'}`;
  const label = title || `${brand?.name || 'Brand'} logo`;

  return (
    <svg
      className={className}
      viewBox="0 0 48 48"
      role="img"
      aria-label={label}
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>{label}</title>
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={primary} />
          <stop offset="100%" stopColor={secondary} />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="48" height="48" rx="11" fill={`url(#${gradientId})`} />
      <path
        d="M13.5 24.5 L20.5 31.5 L34.5 17"
        fill="none"
        stroke="#FFFFFF"
        strokeWidth="4.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
